import { CommunityGlobalSearchResultItem, GlobalSearchScopeIdentifier } from "client/Api/TitleBar/GlobalSearch/globalSearch";
import { useHubStatisticsFormatter } from "client/Features/Hub/useHubStatisticsFormatter";
import { useHighlights } from "client/Features/Layout/TitleBar/GlobalSearch/ResultView/CategoryView/ItemView/useHighlights";
import { NumberFormatter, useKeyboardListNavigationItem } from "client/Hooks";
import { ReactElement } from "react";
import { ItemLayout } from "./ItemLayout";
import {
	FormattedText,
	Highlight,
	HighlightedText,
	Icon,
	Space
} from "client/Components";
import { generateTrackingId, setTrackingId } from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import { useCommunityNavigationHandler } from "client/Features/Layout/TitleBar/GlobalSearch/Navigation/useCommunityNavigationHandler";
import { SearchListItem } from "../SearchListItem";
import { Key } from "client/Misc/commonTypes";

export type CommunityItemViewProps = {
	item: CommunityGlobalSearchResultItem;
	navigationIndex: number;
	itemScope: GlobalSearchScopeIdentifier;
};

export const CommunityItemView = (props: CommunityItemViewProps): ReactElement => {
	const {
		item,
		navigationIndex,
		itemScope
	} = props;

	const { id } = item;

	const executeHandler: () => void = useCommunityNavigationHandler({
		id,
		searchResult: item,
		itemScope
	});

	return (
		<SearchListItem
			onExecute={() => executeHandler()}
			trackingId={generateTrackingId("community-item", `|${id}`)}
		>
			<CommunityItem
				item={item}
				navigationIndex={navigationIndex}
				onExecute={executeHandler}
			/>
		</SearchListItem>
	);
};
CommunityItemView.displayName = "CommunityItemView";

type CommunityItemProps = {
	item: CommunityGlobalSearchResultItem;
	navigationIndex: number;
	onExecute: () => void;
};

const CommunityItem = (props: CommunityItemProps): ReactElement => {
	const {
		item,
		navigationIndex,
		onExecute
	} = props;

	const {
		name,
		memberCount,
		isCurrentUserMember,
		hitHighlights,
		color
	} = item;

	const formatNumber: NumberFormatter = useHubStatisticsFormatter();
	const highlights: Map<string, Highlight> = useHighlights(hitHighlights);

	const { ref, onKeyDown } = useKeyboardListNavigationItem({
		itemIndex: navigationIndex,
		onExecute,
		executeKey: [Key.Enter, Key.Space],
	});

	return (
		<ItemLayout
			ref={ref}
			onKeyDown={onKeyDown}
			tabIndex={-1}
			trackingId="community-info"
		>
			<ItemLayout.IconContainer>
				<ItemLayout.AppColorIcon
					icon="users"
					color={color}
				/>
			</ItemLayout.IconContainer>

			<ItemLayout.InfoWrapper>
				<ItemLayout.Title trackingId="name">
					<HighlightedText
						text={name}
						highlight={highlights.get("name")}
					/>
				</ItemLayout.Title>

				<ItemLayout.Info>
					<ItemLayout.InfoIconWrapper>
						<Icon value="user-group" />
					</ItemLayout.InfoIconWrapper>

					<span
						{...setTrackingId("members-amount")}
					>
						{memberCount === 1
							? localize("_1Member")
							: (
								<FormattedText
									text={localize("_AmountMembers")}
									thingsToReplace={[
										{ what: "{{Amount}}", filler: formatNumber(memberCount) }
									]}
								/>
							)
						}
					</span>

					{isCurrentUserMember && (
						<>
							<Space />|<Space />
							<span
								{...setTrackingId("is-joined")}
							>
								{localize("_Joined")}
							</span>
						</>
					)}
				</ItemLayout.Info>
			</ItemLayout.InfoWrapper>
		</ItemLayout>
	);
};
CommunityItem.displayName = "CommunityItem";
